import { gql, useMutation, useQuery } from "@apollo/client";
import { Select, MenuItem, FormControl, CircularProgress, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Button, Box, Typography, Divider } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NotificationToaster } from "../../utils/NotificationToaster";

// GraphQL Query to fetch all ideas
const GET_IDEAS = gql`
  query GetIdeas {
    ideas {
      id
      title
      createdAt
    }
  }
`;

// GraphQL Mutation to create a new idea
const CREATE_IDEA_GQL = gql`
  mutation CreateIdea($title: String!) {
    createIdea(title: $title) {
      id
      title
      createdAt
    }
  }
`;

// GraphQL Mutation to delete an idea
const DELETE_IDEA_GQL = gql`
  mutation DeleteIdea($deleteIdeaId: ID!) {
    deleteIdea(id: $deleteIdeaId) {
      id
    }
  }
`;

const IdeaDropdown = () => {
    const dispatch = useDispatch();
    // Select the entire idea object from Redux state
    const currentIdeaObject = useSelector((state) => state.explore.idea);

    // State for dialogs and inputs
    const [openNewIdeaDialog, setOpenNewIdeaDialog] = useState(false);
    const [newIdeaTitle, setNewIdeaTitle] = useState("");
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
    const [ideaToDelete, setIdeaToDelete] = useState(null);

    // State to hold the sorted ideas
    const [sortedIdeas, setSortedIdeas] = useState([]);

    // Fetch ideas query
    const { loading: loadingIdeas, error: errorIdeas, data: dataIdeas, refetch: refetchIdeas } = useQuery(GET_IDEAS, {
      pollInterval: 0, // Explicitly disable polling
      fetchPolicy: 'cache-first', // Use cache primarily
    });

    // Effect to handle errors from fetching ideas
    useEffect(() => {
        if (errorIdeas) {
            console.error("Error fetching ideas:", errorIdeas);
            NotificationToaster({ message: "Failed to load ideas.", type: "error" });
        }
    }, [errorIdeas]);

    // Effect to sort ideas and handle selection/sync
    useEffect(() => {
      const currentReduxId = currentIdeaObject?.id;
    //   console.log(`[SORT EFFECT] START - Current Redux ID: ${currentReduxId}`);

      // Only process when dataIdeas is available and not loading
      if (!loadingIdeas && dataIdeas?.ideas) {
        // Log the original data to see the dates
        // console.log('Original ideas data:', dataIdeas.ideas.map(idea => ({ id: idea.id, title: idea.title, createdAt: idea.createdAt })));
        
        // Create a mutable copy and explicitly sort by date strings
        const ideas = [...dataIdeas.ideas];
        
        // Sort by createdAt date in descending order (newest to oldest)
        ideas.sort((a, b) => {
          // Safely parse dates, handling invalid values
          let timeA = 0, timeB = 0; // Default timestamps

          try {
            const timestampA = a.createdAt ? parseInt(a.createdAt, 10) : 0;
            if (!isNaN(timestampA)) { // Check if parsing was successful
              const dateA = new Date(timestampA);
              if (!isNaN(dateA.getTime())) { // Double-check Date object validity
                 timeA = dateA.getTime();
              } else {
                 console.warn(`Created invalid Date object for idea ${a.id} from timestamp: ${timestampA}`);
              }
            } else {
              console.warn(`Could not parse createdAt to integer for idea ${a.id}: ${a.createdAt}`);
            }
          } catch (e) {
            console.error(`Error processing date for idea ${a.id}: ${a.createdAt}`, e);
          }
          
          try {
            const timestampB = b.createdAt ? parseInt(b.createdAt, 10) : 0;
             if (!isNaN(timestampB)) {
              const dateB = new Date(timestampB);
              if (!isNaN(dateB.getTime())) {
                 timeB = dateB.getTime();
              } else {
                 console.warn(`Created invalid Date object for idea ${b.id} from timestamp: ${timestampB}`);
              }
            } else {
              console.warn(`Could not parse createdAt to integer for idea ${b.id}: ${b.createdAt}`);
            }
          } catch (e) {
            console.error(`Error processing date for idea ${b.id}: ${b.createdAt}`, e);
          }
          
          // Newest first - larger dates (more recent) should come first
          return timeB - timeA; // Newest first (descending order)
        });
        
        // console.log('Sorted ideas:', ideas.map(idea => ({ id: idea.id, title: idea.title, createdAt: idea.createdAt })));
        setSortedIdeas(ideas);
      } else if (loadingIdeas) {
        // console.log('[SORT EFFECT] Still loading ideas...');
      } else if (!dataIdeas?.ideas) {
        // console.log('[SORT EFFECT] No ideas data yet.');
      }
    }, [dataIdeas, loadingIdeas, currentIdeaObject?.id]); // Depend only on data, loading state, and the Redux ID

    // Create idea mutation
    const [createIdea, { loading: creatingIdea }] = useMutation(CREATE_IDEA_GQL, {
        onCompleted: (data) => {
            NotificationToaster({ message: "Idea created successfully!", type: "success" });
            refetchIdeas(); // Refetch ideas list
            handleCloseNewIdeaDialog();
            // Automatically select the newly created idea
            const newIdeaData = data.createIdea; // Contains id, title, and createdAt
            dispatch({ type: 'SET_IDEA', payload: newIdeaData }); // Dispatch action with type and payload
        },
        onError: (error) => {
            console.error("Error creating idea:", error);
            NotificationToaster({ message: `Failed to create idea: ${error.message}`, type: "error" });
        }
    });

    // Delete idea mutation
    const [deleteIdea, { loading: deletingIdea }] = useMutation(DELETE_IDEA_GQL, {
        onCompleted: (data) => {
            const deletedIdeaId = data?.deleteIdea?.id;
            // Only clear selection if the deleted idea was the currently selected one
            if (deletedIdeaId && currentIdeaObject && currentIdeaObject.id === deletedIdeaId) {
                console.log('Deleted the currently selected idea. Clearing selection.');
                dispatch({ type: 'SET_IDEA', payload: null });
            }
            NotificationToaster({ message: "Idea deleted successfully!", type: "success" });
            refetchIdeas(); // Refetch ideas list
            handleCloseDeleteConfirm();
        },
        onError: (error) => {
            console.error("Error deleting idea:", error);
            NotificationToaster({ message: `Failed to delete idea: ${error.message}`, type: "error" });
        }
    });

    // Handlers for dialogs
    const handleOpenNewIdeaDialog = () => setOpenNewIdeaDialog(true);
    const handleCloseNewIdeaDialog = () => {
        setOpenNewIdeaDialog(false);
        setNewIdeaTitle(""); // Reset title on close
    };

    const handleOpenDeleteConfirm = (idea) => {
        setIdeaToDelete(idea);
        setOpenDeleteConfirm(true);
    };
    const handleCloseDeleteConfirm = () => {
        setOpenDeleteConfirm(false);
        setIdeaToDelete(null);
    };

    // Handler for creating an idea
    const handleCreateIdea = () => {
        if (!newIdeaTitle.trim()) {
            NotificationToaster({ message: "Idea title cannot be empty.", type: "error" });
            return;
        }
        createIdea({ variables: { title: newIdeaTitle } });
    };

    // Handler for confirming deletion
    const handleConfirmDelete = () => {
        if (ideaToDelete) {
            deleteIdea({ variables: { deleteIdeaId: ideaToDelete.id } });
        }
    };

    // Handler for select change - Modified to accept value directly
    const handleChange = (event) => {
        const newIdeaId = event.target.value;
        console.log(`handleChange triggered with newIdeaId: ${newIdeaId}`);

        // Find the full idea object from the sorted list
        const selectedIdeaObject = sortedIdeas.find(idea => idea.id === newIdeaId);

        if (selectedIdeaObject) {
            console.log('Dispatching setIdea with:', selectedIdeaObject);
            dispatch({ type: 'SET_IDEA', payload: selectedIdeaObject });
        } else {
            console.warn(`handleChange: Could not find idea object for ID: ${newIdeaId}`);
            // Optionally handle the case where the idea isn't found, maybe dispatch null or an empty object
            // dispatch({ type: 'SET_IDEA', payload: null });
        }
    };

    // Determine if we are loading data or performing mutations
    const isLoading = loadingIdeas || creatingIdea || deletingIdea;

    const renderSelectedValue = (selected) => {
        // If loading is finished and no ideas is selected
        if (!loadingIdeas && !selected) {
            return <em style={{ color: 'rgba(0, 0, 0, 0.54)' }}>Select Idea</em>; // Placeholder when list is empty
        }
        // If an idea is selected
        const selectedIdeaObject = sortedIdeas.find(idea => idea.id === selected);
        return selectedIdeaObject ? selectedIdeaObject.title : ''; // Return title or empty string if somehow not found
    };

    return (
        <Box sx={{ width: '100%', position: 'relative', display: 'flex', alignItems: 'center' }}> 
            <FormControl sx={{ m: 0, width: 145 }} size="small"> 
                <Select
                    labelId="idea-select-label"
                    id="idea-select"
                    // Use the ID from the Redux store directly, default to empty string if null/undefined
                    value={currentIdeaObject?.id || ''}
                    displayEmpty // Add this prop
                    onChange={handleChange}
                    // Use renderValue to customize display
                    renderValue={renderSelectedValue} 
                    disabled={isLoading} // Disable when loading ideas or mutating
                    MenuProps={{
                        sx: {
                            '& .MuiPaper-root': {
                                maxHeight: '400px', // Doubled the height
                            },
                        }, 
                    }}
                    sx={{ 
                        backgroundColor: 'white', 
                        '& .MuiSelect-select.MuiInputBase-inputSizeSmall': { // Target the inner select element for small size more specifically
                            paddingTop: '4px !important',
                            paddingBottom: '4px !important',
                            paddingLeft: '4px !important',
                            fontSize: '0.875rem' // Reduce font size for selected value
                        }
                    }} // Added white background and forced padding override
                >
                    {loadingIdeas && <MenuItem disabled><CircularProgress size={20} /></MenuItem>}
                    {/* Add New Idea option at the top */} 
                    {!loadingIdeas && (
                        <>
                            <MenuItem 
                                value="__add_new__" // Special value to distinguish from actual ideas
                                onClick={handleOpenNewIdeaDialog} // Trigger dialog on click
                                sx={{ fontSize: '0.875rem' }} // Reduce font size for menu item
                            >
                                <AddIcon fontSize="small" sx={{ mr: 1 }} />
                                Add New Idea
                            </MenuItem>
                            <Divider />
                        </>
                    )}
                    {/* Dropdown items - map over the sorted data (newest first) */}
                    {sortedIdeas // Map directly over sortedIdeas (already newest first)
                        .map((idea) => (
                            <MenuItem 
                                key={idea.id} 
                                value={idea.id}
                                sx={{ fontSize: '0.875rem' }} // Reduce font size for menu item
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                    <Typography variant="inherit" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{idea.title || `Idea ${idea.id.substring(0,4)}...`}</Typography>
                                    <IconButton 
                                        aria-label="delete idea"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent Select's onChange
                                            handleOpenDeleteConfirm(idea);
                                        }}
                                        size="small"
                                        sx={{ 
                                            ml: 1, // Add some margin
                                            '&:hover': {
                                                color: 'red',
                                            }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </MenuItem>
                        ))
                    }
                </Select>
                {isLoading && <CircularProgress size={24} sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-12px', marginLeft: '-12px' }} />}
            </FormControl>

            {/* New Idea Dialog */}
            <Dialog open={openNewIdeaDialog} onClose={handleCloseNewIdeaDialog}>
                <DialogTitle>Create New Idea</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Enter the title for your new idea.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Idea Title"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={newIdeaTitle}
                        onChange={(e) => setNewIdeaTitle(e.target.value)}
                        disabled={creatingIdea}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseNewIdeaDialog} disabled={creatingIdea}>Cancel</Button>
                    <Button onClick={handleCreateIdea} disabled={creatingIdea} variant="contained">
                        {creatingIdea ? <CircularProgress size={24} /> : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDeleteConfirm}
                onClose={handleCloseDeleteConfirm}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the idea "{ideaToDelete?.title}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteConfirm} disabled={deletingIdea}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" disabled={deletingIdea} variant="contained">
                        {deletingIdea ? <CircularProgress size={24} /> : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default IdeaDropdown;
